// src/components/FeedList.js
import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/AuthContext";

function FeedList({ feeds }) {
  const { user } = useAuth();
  const [feedSections, setFeedSections] = useState([]);
  const [loadingFeeds, setLoadingFeeds] = useState(new Set());
  const [savedArticles, setSavedArticles] = useState(new Set());
  const [sports, setSports] = useState([]);
  const [selectedSports, setSelectedSports] = useState({});

  // Styles
  const styles = {
    container: {
      backgroundColor: "white",
      borderRadius: "8px",
      padding: "20px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
    },
    mainTitle: {
      fontSize: "20px",
      fontWeight: "600",
      color: "#2c3e50",
      marginTop: "0",
      marginBottom: "20px",
      paddingBottom: "10px",
      borderBottom: "1px solid #e9ecef",
    },
    feedSection: {
      marginBottom: "30px",
      padding: "15px",
      backgroundColor: "#f8f9fa",
      borderRadius: "6px",
      border: "1px solid #e9ecef",
    },
    feedSourceHeader: {
      fontSize: "18px",
      fontWeight: "600",
      color: "#2c3e50",
      marginBottom: "15px",
      padding: "10px 15px",
      backgroundColor: "#ffffff",
      borderRadius: "4px",
      border: "1px solid #dee2e6",
      display: "flex",
      alignItems: "center",
      gap: "10px",
    },
    feedSourceIcon: {
      fontSize: "16px",
      color: "#3498db",
    },
    loadingSection: {
      textAlign: "center",
      padding: "20px",
      backgroundColor: "#f8f9fa",
      borderRadius: "6px",
      marginBottom: "20px",
      border: "1px solid #e9ecef",
    },
    loadingSpinner: {
      display: "inline-block",
      width: "20px",
      height: "20px",
      border: "3px solid #f3f3f3",
      borderTop: "3px solid #3498db",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      marginRight: "10px",
    },
    loadingText: {
      color: "#6c757d",
      fontSize: "14px",
      marginTop: "10px",
    },
    list: {
      listStyle: "none",
      padding: "0",
      margin: "0",
    },
    listItem: {
      padding: "15px",
      borderBottom: "1px solid #e9ecef",
      backgroundColor: "white",
      marginBottom: "5px",
      borderRadius: "4px",
      position: "relative",
    },
    buttons: {
      display: "flex",
      gap: "10px",
      marginBottom: "10px",
    },
    deleteButton: {
      backgroundColor: "#e74c3c",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    saveButton: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      fontWeight: "500",
      transition: "background-color 0.2s",
    },
    sportSelect: {
      padding: "4px 8px",
      fontSize: "12px",
      border: "1px solid #ced4da",
      borderRadius: "4px",
      marginRight: "10px",
      backgroundColor: "white",
      color: "#495057",
      minWidth: "120px",
    },
    savedButton: {
      backgroundColor: "#2ecc71",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "not-allowed",
      fontSize: "12px",
      fontWeight: "500",
    },
    savingButton: {
      backgroundColor: "#f39c12",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      cursor: "not-allowed",
      fontSize: "12px",
      fontWeight: "500",
    },
    articleTitle: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#2c3e50",
      textDecoration: "none",
      display: "block",
      marginBottom: "5px",
      transition: "color 0.2s",
    },
    date: {
      fontSize: "12px",
      color: "#6c757d",
    },
    errorSection: {
      backgroundColor: "#f8d7da",
      color: "#721c24",
      padding: "15px",
      borderRadius: "6px",
      marginBottom: "20px",
      border: "1px solid #f5c6cb",
    },
    errorHeader: {
      fontWeight: "600",
      marginBottom: "5px",
    },
    noArticlesMessage: {
      textAlign: "center",
      padding: "20px",
      color: "#6c757d",
      fontStyle: "italic",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 0",
      color: "#6c757d",
      fontSize: "16px",
    },
  };

  // Add CSS animation for spinner
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Fetch sports data
  useEffect(() => {
    const fetchSports = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/rss/api/sports`
        );
        
      if (response.ok) {
        const responseData = await response.json();
        console.log("Sports API response:", responseData);
        setSports(responseData.sports || []);
      }
      } catch (error) {
        console.error("Error fetching sports:", error);
      }
    };

    fetchSports();
  }, []);

  useEffect(() => {
    const fetchArticlesFromFeeds = async () => {
      if (feeds.length === 0) {
        setFeedSections([]);
        return;
      }

      // Create initial loading states
      const newLoadingFeeds = new Set(
        feeds.map((feed, index) =>
          typeof feed === "string" ? feed : `${feed.url}-${index}`
        )
      );
      setLoadingFeeds(newLoadingFeeds);

      const feedPromises = feeds.map(async (feed, index) => {
        const feedUrl = typeof feed === "string" ? feed : feed.url;
        const feedName =
          typeof feed === "string" ? `RSS Feed ${index + 1}` : feed.name;
        const feedKey =
          typeof feed === "string" ? feed : `${feed.url}-${index}`;

        try {
        const response = await fetch(
          `${
            process.env.REACT_APP_BASE_URL
          }/rss/api/fetch-rss?url=${encodeURIComponent(feedUrl)}`
        );

          if (!response.ok) {
            throw new Error(
              `Failed to fetch feed: ${response.status} ${response.statusText}`
            );
          }

          const articles = await response.json();

          // Remove from loading state
          setLoadingFeeds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(feedKey);
            return newSet;
          });

          return {
            id: feedKey,
            name: feedName,
            url: feedUrl,
            articles: articles || [],
            error: null,
          };
        } catch (error) {
          console.error(`Error fetching RSS feed ${feedName}:`, error);

          // Remove from loading state
          setLoadingFeeds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(feedKey);
            return newSet;
          });

          return {
            id: feedKey,
            name: feedName,
            url: feedUrl,
            articles: [],
            error: error.message,
          };
        }
      });

      try {
        const results = await Promise.all(feedPromises);
        setFeedSections(results);
      } catch (error) {
        console.error("Error processing feeds:", error);
      }
    };

    fetchArticlesFromFeeds();
  }, [feeds]);

  const formatDate = (dateString) => {
    try {
      const options = { year: "numeric", month: "long", day: "numeric" };
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, options);
    } catch {
      return "Unknown date";
    }
  };

  const handleDelete = (feedId, articleIndex) => {
    setFeedSections((prevSections) =>
      prevSections.map((section) =>
        section.id === feedId
          ? {
              ...section,
              articles: section.articles.filter((_, i) => i !== articleIndex),
            }
          : section
      )
    );

    // Remove from saved articles if it was saved
    const articleKey = `${feedId}-${articleIndex}`;
    setSavedArticles((prev) => {
      const newSet = new Set(prev);
      newSet.delete(articleKey);
      return newSet;
    });
  };

  const handleSave = async (article, feedId, articleIndex) => {
    const articleKey = `${feedId}-${articleIndex}`;
    const selectedSportId = selectedSports[articleKey];

    // Validate that a sport is selected
    if (!selectedSportId) {
      alert("Please select a sport before saving the article.");
      return;
    }

    // Add to saving state
    setSavedArticles((prev) => new Set([...prev, `saving-${articleKey}`]));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/rss/api/save-article`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            title: article.title.trim(),
            link: article.link.trim(),
            pubDate: article.pubDate,
            content: article.content || article.contentSnippet || "",
            creator: article.creator || article.author || "Unknown",
            sport_id: selectedSportId,
            admin_id: user?.id || user?.uid || null,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to save article");
      }

      if (data.success) {
        // Remove saving state and add saved state
        setSavedArticles((prev) => {
          const newSet = new Set(prev);
          newSet.delete(`saving-${articleKey}`);
          newSet.add(articleKey);
          return newSet;
        });
        alert("Article saved successfully!");
      } else {
        throw new Error(data.error || data.message || "Failed to save article");
      }
    } catch (error) {
      console.error("Failed to save the article:", error);
      // Remove saving state
      setSavedArticles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(`saving-${articleKey}`);
        return newSet;
      });
      alert(`Error saving article: ${error.message}`);
    }
  };

  const getArticleButtonStyle = (feedId, articleIndex) => {
    const articleKey = `${feedId}-${articleIndex}`;
    const isSaving = savedArticles.has(`saving-${articleKey}`);
    const isSaved = savedArticles.has(articleKey);

    if (isSaving) return styles.savingButton;
    if (isSaved) return styles.savedButton;
    return styles.saveButton;
  };

  const getArticleButtonText = (feedId, articleIndex) => {
    const articleKey = `${feedId}-${articleIndex}`;
    const isSaving = savedArticles.has(`saving-${articleKey}`);
    const isSaved = savedArticles.has(articleKey);

    if (isSaving) return "Saving...";
    if (isSaved) return "Saved";
    return "Save into DB";
  };

  const getArticleButtonDisabled = (feedId, articleIndex) => {
    const articleKey = `${feedId}-${articleIndex}`;
    return (
      savedArticles.has(`saving-${articleKey}`) || savedArticles.has(articleKey)
    );
  };

  const handleSportChange = (feedId, articleIndex, sportId) => {
    const articleKey = `${feedId}-${articleIndex}`;
    setSelectedSports(prev => ({
      ...prev,
      [articleKey]: sportId
    }));
  };

  if (feeds.length === 0) {
    return (
      <div style={styles.container} className="rss-feedlist">
        <style>{`
          @media (max-width: 1024px) {
            .rss-feedlist { padding: 16px; }
          }
          @media (max-width: 768px) {
            .rss-feedlist { padding: 14px; }
            .rss-feed-actions { flex-direction: column; align-items: stretch; }
            .rss-feed-actions button,
            .rss-feed-actions select { width: 100%; }
          }
          @media (max-width: 480px) {
            .rss-feedlist { padding: 12px; }
          }
        `}</style>
        <h2 style={styles.mainTitle}>RSS Sports News</h2>
        <div style={styles.emptyState}>
          No RSS feeds added yet. Add a feed using the form above to see
          articles here.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container} className="rss-feedlist">
      <style>{`
        @media (max-width: 1024px) {
          .rss-feedlist { padding: 16px; }
        }
        @media (max-width: 768px) {
          .rss-feedlist { padding: 14px; }
          .rss-feed-actions { flex-direction: column; align-items: stretch; }
          .rss-feed-actions button,
          .rss-feed-actions select { width: 100%; }
          .rss-feed-section { padding: 12px; }
        }
        @media (max-width: 480px) {
          .rss-feedlist { padding: 12px; }
          .rss-feed-section { margin-bottom: 20px; }
        }
      `}</style>
      <h2 style={styles.mainTitle}>RSS Sports News</h2>

      {/* Show loading sections for feeds still being fetched */}
      {Array.from(loadingFeeds).map((feedKey) => {
        const feedInfo = feeds.find(
          (feed, index) =>
            (typeof feed === "string" ? feed : `${feed.url}-${index}`) ===
            feedKey
        );
        const feedName =
          typeof feedInfo === "string"
            ? `RSS Feed ${feeds.indexOf(feedInfo) + 1}`
            : feedInfo?.name || "RSS Feed";

        return (
          <div key={feedKey} style={styles.loadingSection}>
            <div style={styles.feedSourceHeader}>
              <span style={styles.feedSourceIcon}>📰</span>
              {feedName}
            </div>
            <div>
              <div style={styles.loadingSpinner}></div>
              <span>Loading articles...</span>
            </div>
            <div style={styles.loadingText}>
              Fetching latest news from this RSS feed
            </div>
          </div>
        );
      })}

      {/* Show completed feed sections */}
      {feedSections.map((section) => (
        <div key={section.id} style={styles.feedSection} className="rss-feed-section">
          <div style={styles.feedSourceHeader}>
            <span style={styles.feedSourceIcon}>📰</span>
            {section.name}
            <span
              style={{ fontSize: "12px", color: "#6c757d", marginLeft: "auto" }}
            >
              {section.articles.length} articles
            </span>
          </div>

          {section.error ? (
            <div style={styles.errorSection}>
              <div style={styles.errorHeader}>Failed to load feed</div>
              <div>{section.error}</div>
              <div style={{ fontSize: "12px", marginTop: "5px", opacity: 0.8 }}>
                URL: {section.url}
              </div>
            </div>
          ) : section.articles.length > 0 ? (
            <ul style={styles.list}>
              {section.articles.map((article, index) => (
                <li key={index} style={styles.listItem}>
                  <div style={styles.buttons} className="rss-feed-actions">
                    <select
                      style={styles.sportSelect}
                      value={selectedSports[`${section.id}-${index}`] || ""}
                      onChange={(e) => handleSportChange(section.id, index, e.target.value)}
                      disabled={getArticleButtonDisabled(section.id, index)}
                    >
                      <option value="">Select Sport...</option>
                      {Array.isArray(sports) && sports.length > 0 ? (
                        sports.map(sport => (
                          <option key={sport.id} value={sport.id}>
                            {sport.name}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>Loading sports...</option>
                      )}
                    </select>
                    <button
                      style={styles.deleteButton}
                      onClick={() => handleDelete(section.id, index)}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = "#c0392b";
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = "#e74c3c";
                      }}
                    >
                      Delete from UI
                    </button>
                    <button
                      style={getArticleButtonStyle(section.id, index)}
                      onClick={() => handleSave(article, section.id, index)}
                      disabled={getArticleButtonDisabled(section.id, index)}
                      onMouseOver={(e) => {
                        if (!getArticleButtonDisabled(section.id, index)) {
                          e.currentTarget.style.backgroundColor = "#2980b9";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!getArticleButtonDisabled(section.id, index)) {
                          e.currentTarget.style.backgroundColor = "#3498db";
                        }
                      }}
                    >
                      {getArticleButtonText(section.id, index)}
                    </button>
                  </div>
                  <a
                    href={article.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.articleTitle}
                    onMouseOver={(e) => {
                      e.currentTarget.style.color = "#3498db";
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.color = "#2c3e50";
                    }}
                  >
                    {article.title}
                  </a>
                  <br />
                  <small style={styles.date}>
                    Published on: {formatDate(article.pubDate)}
                  </small>
                </li>
              ))}
            </ul>
          ) : (
            <div style={styles.noArticlesMessage}>
              No articles found in this RSS feed
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default FeedList;
